import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Inbox, Archive, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { useMessageEncryption } from '@/hooks/useMessageEncryption';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConversationView } from '@/components/ConversationView';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';

const ITEMS_PER_PAGE = 20;

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  order_id: string | null;
  dispute_id: string | null;
  subject: string;
  content: string;
  message?: string;
  is_read: boolean;
  created_at: string;
  sender?: {
    full_name: string;
    role: string;
  };
}

const MessagingCenter = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState<{
    otherUserId: string;
    otherUserName: string;
    subject: string;
  } | null>(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const { decryptMessages } = useMessageEncryption();

  const handleRefresh = async () => {
    setCurrentPage(0);
    setHasMore(true);
    await fetchMessages(true);
  };

  const { containerRef, shouldShowIndicator, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    threshold: 80
  });

  const { observerTarget } = useInfiniteScroll({
    onLoadMore: () => fetchMessages(false, currentPage + 1),
    hasMore,
    isLoading: loadingMore
  });

  useEffect(() => {
    if (user) {
      handleRefresh();
      
      // Subscribe to new messages
      const channel = supabase
        .channel('messages_changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${user.id}`
          },
          () => {
            handleRefresh();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const fetchMessages = async (isRefresh = false, page = 0) => {
    try {
      if (isRefresh) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data: messagesData, error } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user?.id},recipient_id.eq.${user?.id}`)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Fetch sender profiles separately
      if (messagesData && messagesData.length > 0) {
        const senderIds = [...new Set(messagesData.map(m => m.sender_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, system_role')
          .in('id', senderIds);

        const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);
        
        const enrichedMessages = messagesData.map(msg => ({
          ...msg,
          sender: profilesMap.get(msg.sender_id) || { full_name: 'Utilisateur', role: 'client' }
        }));

        // Decrypt messages
        const decryptedMessages = await decryptMessages(enrichedMessages as any);

        if (isRefresh || page === 0) {
          setMessages(decryptedMessages as Message[]);
        } else {
          setMessages(prev => [...prev, ...(decryptedMessages as Message[])]);
        }

        setCurrentPage(page);
        setHasMore(messagesData.length === ITEMS_PER_PAGE);
      } else {
        setMessages([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(t('messaging.errorLoading'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleOpenMessage = (message: Message) => {
    const otherUserId = message.sender_id === user?.id ? message.recipient_id : message.sender_id;
    const otherUserName = message.sender_id === user?.id 
      ? 'Destinataire' 
      : message.sender?.full_name || 'Utilisateur';
    
    setSelectedConversation({
      otherUserId,
      otherUserName,
      subject: message.subject
    });
    
    if (!message.is_read && message.recipient_id === user?.id) {
      handleMarkAsRead(message.id);
    }
  };

  // Group messages by conversation (same participants)
  const groupedMessages = messages.reduce((acc, msg) => {
    const otherUserId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
    if (!acc[otherUserId]) {
      acc[otherUserId] = [];
    }
    acc[otherUserId].push(msg);
    return acc;
  }, {} as Record<string, Message[]>);

  const conversationsList = Object.entries(groupedMessages).map(([otherUserId, msgs]) => {
    const latestMsg = msgs[0]; // Messages are already sorted by date DESC
    const unreadCount = msgs.filter(m => m.recipient_id === user?.id && !m.is_read).length;
    const otherUserName = latestMsg.sender_id === user?.id 
      ? 'Vous' 
      : latestMsg.sender?.full_name || 'Utilisateur';
    
    return {
      otherUserId,
      otherUserName,
      latestMsg,
      unreadCount,
      messageCount: msgs.length
    };
  });

  const unreadCount = messages.filter(m => m.recipient_id === user?.id && !m.is_read).length;

  if (loading) {
    return <div className="animate-pulse">{t('messaging.loading')}</div>;
  }

  if (selectedConversation) {
    return (
      <ConversationView
        otherUserId={selectedConversation.otherUserId}
        otherUserName={selectedConversation.otherUserName}
        initialSubject={selectedConversation.subject}
        onBack={() => {
          setSelectedConversation(null);
          fetchMessages();
        }}
      />
    );
  }

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Pull to Refresh Indicator */}
      {shouldShowIndicator && (
        <div className="fixed top-16 left-0 right-0 z-50 flex justify-center">
          <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm">
              {isRefreshing ? t('messaging.refreshing') : t('messaging.pullToRefresh')}
            </span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg md:text-2xl font-bold truncate">{t('messaging.title')}</h2>
          <p className="text-xs md:text-sm text-muted-foreground truncate">
            {t('messaging.description')}
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="destructive" className="text-[10px] md:text-xs whitespace-nowrap flex-shrink-0">{unreadCount} {t('messaging.unread')}</Badge>
        )}
      </div>

      <div className="space-y-4">
        {conversationsList.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('messaging.noConversations')}</p>
            </CardContent>
          </Card>
        ) : (
          conversationsList.map((conv) => (
            <Card
              key={conv.otherUserId}
              className={`cursor-pointer transition-all hover:shadow-md ${
                conv.unreadCount > 0 ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => handleOpenMessage(conv.latestMsg)}
            >
              <CardHeader className="p-3 md:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                      <CardTitle className="text-xs md:text-base truncate max-w-[200px] md:max-w-none">
                        {conv.latestMsg.subject}
                      </CardTitle>
                      {conv.unreadCount > 0 && (
                        <Badge variant="default" className="text-[10px] md:text-xs whitespace-nowrap">{conv.unreadCount} nouveau(x)</Badge>
                      )}
                    </div>
                    <CardDescription className="text-xs truncate">
                      {conv.latestMsg.sender_id === user?.id ? 'Vous' : conv.latestMsg.sender?.full_name || 'Utilisateur'}
                      {conv.latestMsg.sender?.role === 'admin' && ' (Administrateur)'}
                      {conv.latestMsg.sender?.role === 'vendor' && ' (Vendeur)'}
                      {' • '}
                      {conv.messageCount} message{conv.messageCount > 1 ? 's' : ''}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-[10px] md:text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(conv.latestMsg.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                    {conv.latestMsg.content || conv.latestMsg.message}
                  </p>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>

      {/* Infinite Scroll Observer */}
      {hasMore && !loading && conversationsList.length > 0 && (
        <div ref={observerTarget} className="py-8 flex justify-center">
          {loadingMore && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{t('messaging.loading')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagingCenter;
